import { Checkbox } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '../../hooks/useBoolean';
import { DataItem, updateDailyTempWorker } from '../../services/apiSLTP/activities';
import { createDailyTempWorker, DailyTempWorker, deleteDailyTempWorker } from '../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getPriceTypeInfos, stepModes, systemConfigKeyEnum } from '../../utils/enums';
import DraggableCell from '../DraggableCell';
import DroppableCell from '../DroppableCell';
import ManageDailyTempWorkerModal from '../modals/operations/planning/ManageDailyTempWorkerModal';
import IconButton from '../shared/IconButton';
import { useSystemConfig } from '../../hooks/useSystemConfig';

interface TempWorkersCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    hiddenPlusIcon?: boolean;
    refetch?: () => void;
}

const TempWorkersCell: React.FC<TempWorkersCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    hiddenPlusIcon = false,
    refetch = () => {},
}) => {
    const manageModal = useBoolean(false);
    const [selectedItem, setSelectedItem] = useState<DailyTempWorker | null>(null);
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const itemType = 'TEMPWORKERS';

    const handleTempWorkersChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            await updateDailyTempWorker(fromItemId, { activityId: toLineId });
            refetch();
        } catch (error) {
            console.error('Error updating daily temp worker:', error);
        }
    };

    const handleDeleteTempWorker = async (id: string, index: number) => {
        try {
            await deleteDailyTempWorker(id);
            refetch();
        } catch (error) {
            console.error('Error deleting daily temp worker:', error);
        }
    };

    const handleCreateDailyTempWorker = async (dailyTempWorkerId: string) => {
        const selectedTempWorker = activity.dailyTempWorkers.find((value) => value._id === dailyTempWorkerId);
        if (selectedTempWorker) {
            const response = await createDailyTempWorker({
                activityId: activity._id,
                tempWorkerTypeId: selectedTempWorker.tempWorkerTypeId._id,
                durationHours: selectedTempWorker.durationHours,
                tempWorkerName: selectedTempWorker.tempWorkerName,
                includedInExpenses: selectedTempWorker.includedInExpenses,
                price: selectedTempWorker.price,
                priceType: selectedTempWorker.priceType,
            });
            refetch();
        }
    };

    return (
        <div className={`${'text-black'}`} style={{ display: 'flex', height: '100%', width: '100%', opacity: disabled ? 0.7 : 1 }}>
            <DroppableCell
                dropId={{ lineId: activity._id }}
                moveCell={(fromLineId, fromItemId, toLineId) => handleTempWorkersChange(fromLineId, fromItemId, toLineId)}
                itemType={itemType}
                disabled={disabled}
            >
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => {
                                    manageModal.setTrue();
                                    setSelectedItem(null);
                                }}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addTempWorker'))}
                            />
                        )}
                    </div>
                    <div className="  gap-5px fx-c">
                        {activity.dailyTempWorkers.map((tempWorker, index) => (
                            <div key={`${activityIndex}-${index}`} className="fx-r fx-fg-1 gap-5px fx-items-fs">
                                <DraggableCell
                                    key={`${activityIndex}-${index}`}
                                    dragIds={{ itemId: tempWorker._id, lineId: activity._id }}
                                    index={index}
                                    itemType={itemType}
                                    onDelete={() => handleDeleteTempWorker(tempWorker._id, index)}
                                    disabled={disabled}
                                    state={tempWorker.state}
                                    onDuplicate={() => handleCreateDailyTempWorker(tempWorker._id)}
                                    onDoubleClick={() => {
                                        manageModal.setTrue();
                                        setSelectedItem(tempWorker);
                                    }}
                                >
                                    <div
                                        title={tempWorker.durationHours + t('hourAbreviation') + ' - ' + tempWorker.tempWorkerTypeId.designation}
                                        className=" one-line-container"
                                    >
                                        <div className="fx-r fx-fg-1 fx-justify-sb">
                                            <div className="font-w-700 one-line">
                                                {tempWorker.tempWorkerTypeId.designation}
                                                {tempWorker.durationHours && ' - ' + tempWorker.durationHours + t('hourAbreviation')}
                                            </div>
                                        </div>
                                        {activity.stepMode === stepModes.VALIDATE && (
                                            <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                                <div className="one-line" title={tempWorker.tempWorkerTypeId.codeProC}>
                                                    {tempWorker.tempWorkerTypeId.codeProC}
                                                </div>
                                            </div>
                                        )}
                                        <div className="one-line">{tempWorker.tempWorkerName}</div>
                                        {activity.stepMode === stepModes.VALIDATE &&
                                            typeof tempWorker.price === 'number' &&
                                            typeof tempWorker.priceType === 'number' && (
                                                <div
                                                    className="one-line  font-italic"
                                                    style={{
                                                        color: '#DC4D23',
                                                        textDecorationLine:
                                                            tempWorker?.includedInExpenses && activity.includedInExpenses
                                                                ? undefined
                                                                : 'line-through',
                                                    }}
                                                >
                                                    {tempWorker.price}{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}/{capitalizeFirstLetter(t(getPriceTypeInfos(tempWorker.priceType)))}
                                                </div>
                                            )}
                                    </div>
                                </DraggableCell>
                                {activity.stepMode === stepModes.VALIDATE && (
                                    <div className="h-25px fx-r fx-items-c">
                                        <Checkbox
                                            checked={tempWorker.includedInExpenses}
                                            onChange={async (_, checked) => {
                                                await updateDailyTempWorker(tempWorker._id, { includedInExpenses: checked });
                                                refetch();
                                            }}
                                            color="success"
                                            sx={{ padding: 0 }}
                                            disabled={disabled || !activity.includedInExpenses}
                                            title={capitalizeFirstLetter(t('includedInExpenses'))}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DroppableCell>
            {manageModal.value && (
                <ManageDailyTempWorkerModal
                    open={manageModal.value}
                    activity={activity}
                    handleClose={() => manageModal.setFalse()}
                    refetch={refetch}
                    dailyTempWorker={selectedItem}
                />
            )}
        </div>
    );
};

export default TempWorkersCell;
